/**
 * Health Plugin wrapper for Slimmy.
 *
 * Built on top of `@capgo/capacitor-health` (HealthKit on iOS, Health
 * Connect on Android). Exposes a small domain-specific API that the
 * React UI calls — `requestAuthorization`, `getSteps`, `getWeight`,
 * `writeWeight`, `syncHealthDataToBackend`.
 *
 * The plugin returns raw samples; we aggregate steps by local day here
 * because the plugin (v7) doesn't have a `queryAggregated`/`bucket`
 * helper. Aggregation is fast (max ~7 days × few samples).
 */
import { Capacitor } from '@capacitor/core';
import { Health } from '@capgo/capacitor-health';

export const isNative = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform();

/**
 * Convert any Date / ISO string to a local YYYY-MM-DD key.
 */
const toLocalDateKey = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Ask the OS for permission to read/write health data. Apple/Google
 * are deliberately opaque about which permissions were granted (no way
 * to read back the "read" permissions on iOS by design), so we infer
 * success by trying to read a single sample after authorization.
 */
export const requestAuthorization = async (dataTypes = ['steps', 'distance', 'calories', 'heartRate', 'weight']) => {
  try {
    if (!isNative()) {
      return { success: false, permissions: [], error: 'not implemented (web)' };
    }
    const avail = await Health.isAvailable();
    if (!avail?.available) {
      return { success: false, permissions: [], error: avail?.reason || 'unavailable' };
    }

    // Read = whatever the caller asked for (default = all supported
    // by the plugin). Write = only weight, since that's the only one
    // we ever write back to HealthKit from Slimmy.
    const read = Array.from(new Set(dataTypes || []));
    const write = read.includes('weight') ? ['weight'] : [];

    const status = await Health.requestAuthorization({ read, write });

    // iOS hides read permissions for privacy: we treat the dialog as
    // success if it didn't throw. Android returns proper readAuthorized.
    const granted = (status?.readAuthorized || []).concat(status?.writeAuthorized || []);
    return {
      success: true,
      permissions: granted.length > 0 ? granted : read,
    };
  } catch (e) {
    console.error('[Health] requestAuthorization error', e);
    const msg = String(e?.message || e || '');
    return { success: false, permissions: [], error: msg };
  }
};

export const isHealthDataAvailable = async () => {
  try {
    if (!isNative()) return { available: false, provider: null };
    const r = await Health.isAvailable();
    return {
      available: !!r?.available,
      provider: getPlatform() === 'ios' ? 'apple_health' : 'google_fit',
      reason: r?.reason,
    };
  } catch (e) {
    return { available: false, provider: null, error: String(e?.message || e) };
  }
};

/**
 * Returns daily step totals aggregated client-side from raw samples.
 * [{ date: 'YYYY-MM-DD', count: 1234 }, ...]
 *
 * IMPORTANT — Source deduplication:
 * On iOS, Apple HealthKit collects step samples from MULTIPLE sources
 * (iPhone CMPedometer, Apple Watch, third-party apps like Fitbit/Garmin).
 * If a user has both iPhone + Apple Watch, the same steps are recorded
 * by BOTH devices — naively summing all samples doubles (or triples)
 * the count vs what Apple Salute shows.
 *
 * Apple Health UI uses HKStatisticsQuery internally with `separateBySource`
 * and picks the most complete source per day (de facto deduplication).
 * The Capgo plugin v7 doesn't expose that API, so we mimic the behaviour
 * here: group samples by (date, sourceName), then take the MAX across
 * sources for each date. This matches what the user sees in Salute.
 */
export const getSteps = async (startDate, endDate) => {
  try {
    if (!isNative()) return { steps: [] };
    const res = await Health.readSamples({
      dataType: 'steps',
      startDate,
      endDate,
      limit: 5000,
      ascending: true,
    });
    // Group samples by (date, sourceName) — sum within each source.
    const perSource = {}; // { '2026-02-25': { 'iPhone': 1234, 'Watch': 1800 } }
    for (const s of (res?.samples || [])) {
      const key = toLocalDateKey(s.startDate);
      const src = s.sourceName || s.sourceId || 'unknown';
      if (!perSource[key]) perSource[key] = {};
      perSource[key][src] = (perSource[key][src] || 0) + Math.round(s.value || 0);
    }
    // For each date, pick the MAX across sources (Apple Health "most
    // complete source" semantic). This avoids double-counting steps
    // when iPhone + Watch both recorded the same walks.
    const steps = Object.entries(perSource).map(([date, sources]) => {
      const max = Math.max(0, ...Object.values(sources));
      return { date, count: max };
    }).sort((a, b) => (a.date < b.date ? -1 : 1));
    return { steps };
  } catch (e) {
    console.error('[Health] getSteps error', e);
    return { steps: [], error: String(e?.message || e) };
  }
};

/**
 * Returns weight history. Plugin always normalizes to kilogram via the
 * `unit` field; we keep the latest measurement per day to avoid spam.
 */
export const getWeight = async (startDate, endDate) => {
  try {
    if (!isNative()) return { weights: [] };
    const res = await Health.readSamples({
      dataType: 'weight',
      startDate,
      endDate,
      limit: 500,
      ascending: true,
    });
    const latestByDay = {};
    for (const s of (res?.samples || [])) {
      let kg = s.value;
      // Plugin should already return kg, but guard anyway.
      if (s.unit && s.unit !== 'kilogram') {
        // No other unit possible for weight in this plugin version,
        // but keep the branch so future versions don't surprise us.
        continue;
      }
      const key = toLocalDateKey(s.startDate);
      const t = new Date(s.startDate).getTime();
      if (!latestByDay[key] || latestByDay[key]._t < t) {
        latestByDay[key] = { date: key, kg, _t: t };
      }
    }
    const weights = Object.values(latestByDay)
      .map(({ date, kg }) => ({ date, kg }))
      .sort((a, b) => (a.date < b.date ? -1 : 1));
    return { weights };
  } catch (e) {
    console.error('[Health] getWeight error', e);
    return { weights: [], error: String(e?.message || e) };
  }
};

/** Workouts/active energy not used in Phase 1 — keep stubs so existing UI code doesn't blow up. */
export const getWorkouts = async () => ({ workouts: [] });
export const getActiveEnergy = async () => ({ calories: [] });

/**
 * Writes a single weight entry back into Apple Health / Health Connect.
 * Called from `InserisciPeso` whenever the user logs their weight in
 * the app — keeps the system Salute app in sync.
 */
export const writeWeight = async (kg, date = new Date().toISOString()) => {
  try {
    if (!isNative()) return { success: false };
    await Health.saveSample({
      dataType: 'weight',
      value: Number(kg),
      unit: 'kilogram',
      startDate: typeof date === 'string' ? date : date.toISOString(),
    });
    return { success: true };
  } catch (e) {
    console.error('[Health] writeWeight error', e);
    return { success: false, error: String(e?.message || e) };
  }
};

/**
 * Push the user's last 7 days of steps + weight to our backend so the
 * dashboard / challenges can use them server-side.
 *
 * IMPORTANT: We deliberately use axios (not native fetch) here because
 * Capacitor's WKWebView on iOS does NOT send first-party cookies on
 * cross-origin fetch() calls with `credentials: 'include'` — even when
 * the cookie WAS set by the same origin. axios (with withCredentials)
 * routes through XHR which DOES include them, matching the rest of
 * the app's authenticated calls.
 */
export const syncHealthDataToBackend = async (apiUrl, days = 7) => {
  const axios = (await import('axios')).default;
  const endDate = new Date().toISOString();
  const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
  const synced = { steps: 0, weights: 0, workouts: 0, calories: 0 };
  const errors = {};

  try {
    const platform = getPlatform();
    const provider = platform === 'ios' ? 'apple_health' : 'google_fit';

    const stepsData = await getSteps(startDate, endDate);
    if (stepsData.error) errors.steps_read = stepsData.error;
    if (stepsData.steps?.length > 0) {
      try {
        const r = await axios.post(
          `${apiUrl}/api/health/sync/steps`,
          { provider, steps: stepsData.steps },
          { withCredentials: true }
        );
        synced.steps = r.data?.synced_count || 0;
      } catch (e) {
        errors.steps_post = e.response?.status
          ? `HTTP ${e.response.status}: ${JSON.stringify(e.response.data || {}).slice(0, 200)}`
          : String(e?.message || e);
      }
    }

    const weightData = await getWeight(startDate, endDate);
    if (weightData.error) errors.weights_read = weightData.error;
    if (weightData.weights?.length > 0) {
      try {
        const r = await axios.post(
          `${apiUrl}/api/health/sync/weight`,
          { provider, weights: weightData.weights },
          { withCredentials: true }
        );
        synced.weights = r.data?.synced_count || 0;
      } catch (e) {
        errors.weights_post = e.response?.status
          ? `HTTP ${e.response.status}: ${JSON.stringify(e.response.data || {}).slice(0, 200)}`
          : String(e?.message || e);
      }
    }

    return {
      success: true,
      synced,
      // Surface read counts + error map so the UI / diagnostic page
      // can tell the difference between "read 0 samples" (permission
      // missing) and "read 2680 samples but POST failed" (cookie /
      // CORS issue). Without this, both showed up as a green
      // "Synced: 0 passi" toast which is impossible to debug.
      readCounts: {
        steps: stepsData.steps?.length || 0,
        weights: weightData.weights?.length || 0,
      },
      errors: Object.keys(errors).length ? errors : null,
    };
  } catch (e) {
    console.error('[Health] sync error', e);
    return { success: false, synced, error: String(e?.message || e) };
  }
};

export default {
  isNative,
  getPlatform,
  requestAuthorization,
  isHealthDataAvailable,
  getSteps,
  getWeight,
  getWorkouts,
  getActiveEnergy,
  writeWeight,
  syncHealthDataToBackend,
};
